<template>
  <section id="payment-config">
    <b-card class="admin-page-mobile">
      <!-- Page Header -->
      <div class="page-header mb-4">
        <div class="d-flex align-items-center">
          <div class="header-icon ml-3">
            <v-icon size="24" color="white">mdi-credit-card-settings</v-icon>
          </div>
          <div>
            <h4 class="mb-1">تنظیمات درگاه پرداخت</h4>
            <p class="text-muted mb-0">پیکربندی درگاه سداد - بانک ملی ایران</p>
          </div>
        </div>
        <div class="header-status">
          <b-badge :variant="gatewayEnabled ? 'success' : 'danger'" pill class="status-badge">
            <span class="status-dot"></span>
            {{ gatewayEnabled ? 'درگاه فعال' : 'درگاه غیرفعال' }}
          </b-badge>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-center py-5">
        <b-spinner variant="primary" />
        <p class="mt-2">در حال بارگذاری...</p>
      </div>

      <div v-else>
        <!-- Gateway Status Card -->
        <div :class="['gateway-status-card', 'mb-4', { disabled: !gatewayEnabled }]">
          <div class="gateway-status-info">
            <div class="gateway-status-icon">
              <v-icon size="28" color="white">
                {{ gatewayEnabled ? 'mdi-check-circle' : 'mdi-close-circle' }}
              </v-icon>
            </div>
            <div class="gateway-status-text">
              <h5>{{ gatewayEnabled ? 'درگاه پرداخت فعال است' : 'درگاه پرداخت غیرفعال است' }}</h5>
              <p>{{ gatewayEnabled ? 'کاربران می‌توانند از درگاه پرداخت استفاده کنند' : 'کاربران قادر به پرداخت آنلاین نیستند' }}</p>
            </div>
          </div>
          <b-button
            :variant="gatewayEnabled ? 'outline-light' : 'light'"
            :disabled="toggleLoading || !configExists"
            @click="toggleGateway"
          >
            <b-spinner v-if="toggleLoading" small class="ml-2" />
            {{ gatewayEnabled ? 'غیرفعال کردن' : 'فعال کردن' }}
          </b-button>
        </div>

        <!-- Environment Selection -->
        <b-card class="config-card mb-4">
          <template #header>
            <div class="card-header-content">
              <v-icon size="20" color="#0ea5e9" class="ml-2">mdi-cog</v-icon>
              <div>
                <span class="card-title">انتخاب محیط</span>
                <span class="card-subtitle">محیط تست یا عملیاتی را انتخاب کنید</span>
              </div>
            </div>
          </template>

          <div class="env-toggle">
            <label :class="['env-option', { active: config.environment === 'sandbox' }]">
              <input v-model="config.environment" type="radio" value="sandbox">
              <div class="env-radio"></div>
              <div>
                <div class="env-label">محیط تست (Sandbox)</div>
                <div class="env-desc">برای آزمایش و توسعه</div>
              </div>
            </label>
            <label :class="['env-option', { active: config.environment === 'production' }]">
              <input v-model="config.environment" type="radio" value="production">
              <div class="env-radio"></div>
              <div>
                <div class="env-label">محیط عملیاتی (Production)</div>
                <div class="env-desc">پرداخت واقعی</div>
              </div>
            </label>
          </div>

          <div class="url-info mt-4">
            <div class="url-item">
              <span class="url-label">آدرس درخواست پرداخت:</span>
              <div class="url-display">
                <code>{{ sadadUrls.paymentRequest }}</code>
                <b-button variant="link" size="sm" @click="copyUrl(sadadUrls.paymentRequest)">
                  <v-icon size="16">mdi-content-copy</v-icon>
                </b-button>
              </div>
            </div>
            <div class="url-item">
              <span class="url-label">آدرس تایید تراکنش:</span>
              <div class="url-display">
                <code>{{ sadadUrls.verify }}</code>
                <b-button variant="link" size="sm" @click="copyUrl(sadadUrls.verify)">
                  <v-icon size="16">mdi-content-copy</v-icon>
                </b-button>
              </div>
            </div>
          </div>
        </b-card>

        <!-- Credentials -->
        <b-card class="config-card mb-4">
          <template #header>
            <div class="card-header-content">
              <v-icon size="20" color="#22c55e" class="ml-2">mdi-lock</v-icon>
              <div>
                <span class="card-title">اطلاعات احراز هویت</span>
                <span class="card-subtitle">کلیدهای دریافتی از بانک ملی</span>
              </div>
            </div>
          </template>

          <b-alert show variant="info" class="mb-4">
            <v-icon size="18" class="ml-2">mdi-information</v-icon>
            این اطلاعات توسط بانک ملی به شما ارائه شده است. لطفاً آن‌ها را با دقت وارد کنید.
          </b-alert>

          <b-row>
            <b-col md="4">
              <b-form-group label="شناسه پذیرنده (Merchant ID)" label-class="required">
                <b-form-input
                  v-model="config.merchantId"
                  placeholder="مثال: 3065"
                  dir="ltr"
                  class="text-left"
                />
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group label="شناسه ترمینال (Terminal ID)" label-class="required">
                <b-form-input
                  v-model="config.terminalId"
                  placeholder="مثال: P37AP5MF"
                  dir="ltr"
                  class="text-left"
                />
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group label="کلید ترمینال (Terminal Key)" label-class="required">
                <b-input-group>
                  <b-form-input
                    v-model="config.terminalKey"
                    :type="showKey ? 'text' : 'password'"
                    :placeholder="keySaved ? 'کلید ذخیره شده - برای تغییر کلید جدید وارد کنید' : 'کلید ترمینال Base64'"
                    dir="ltr"
                    class="text-left"
                  />
                  <b-input-group-append>
                    <b-button variant="outline-secondary" @click="showKey = !showKey">
                      <v-icon size="18">{{ showKey ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
                    </b-button>
                  </b-input-group-append>
                </b-input-group>
                <small v-if="keySaved" class="text-success">
                  <v-icon size="14" color="success">mdi-check-circle</v-icon>
                  کلید ترمینال ذخیره شده است. فقط در صورت نیاز به تغییر، کلید جدید وارد کنید.
                </small>
                <small v-else class="text-muted">این کلید باید به صورت Base64 باشد</small>
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Test Result -->
          <div v-if="testResult" :class="['test-result', testResult.success ? 'success' : 'error']">
            <v-icon size="20">{{ testResult.success ? 'mdi-check-circle' : 'mdi-close-circle' }}</v-icon>
            <span>{{ testResult.message }}</span>
          </div>

          <div class="card-actions">
            <b-button variant="outline-secondary" :disabled="testLoading" @click="testConnection">
              <b-spinner v-if="testLoading" small class="ml-2" />
              <v-icon v-else size="18" class="ml-2">mdi-refresh</v-icon>
              تست اتصال
            </b-button>
            <b-button variant="primary" :disabled="saveLoading" @click="saveConfig">
              <b-spinner v-if="saveLoading" small class="ml-2" />
              <v-icon v-else size="18" class="ml-2">mdi-content-save</v-icon>
              ذخیره تنظیمات
            </b-button>
          </div>
        </b-card>

        <!-- Callback URL -->
        <b-card class="config-card">
          <template #header>
            <div class="card-header-content">
              <v-icon size="20" color="#f59e0b" class="ml-2">mdi-link</v-icon>
              <div>
                <span class="card-title">آدرس بازگشت (Callback URL)</span>
                <span class="card-subtitle">آدرسی که بانک پس از پرداخت کاربر را به آن هدایت می‌کند</span>
              </div>
            </div>
          </template>

          <b-form-group label="آدرس بازگشت">
            <b-form-input
              v-model="config.callbackUrl"
              placeholder="https://api.4-rah.com/payment/callback"
              dir="ltr"
              class="text-left"
            />
            <small class="text-muted">این آدرس را در تنظیمات درگاه بانک وارد کنید</small>
          </b-form-group>
        </b-card>
      </div>
    </b-card>
  </section>
</template>

<script>
import {
  BCard, BButton, BBadge, BSpinner, BRow, BCol, BFormGroup, BFormInput,
  BInputGroup, BInputGroupAppend, BAlert,
} from 'bootstrap-vue'
import PaymentService from '@/services/payment.service'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BCard,
    BButton,
    BBadge,
    BSpinner,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BAlert,
  },
  data() {
    return {
      loading: true,
      saveLoading: false,
      testLoading: false,
      toggleLoading: false,
      configExists: false,
      gatewayEnabled: false,
      showKey: false,
      testResult: null,
      keySaved: false,
      config: {
        merchantId: '',
        terminalId: '',
        terminalKey: '',
        environment: 'sandbox',
        callbackUrl: 'https://api.4-rah.com/payment/callback',
      },
    }
  },
  computed: {
    sadadUrls() {
      const base = this.config.environment === 'production'
        ? 'https://sadad.shaparak.ir/VPG'
        : 'https://sandbox.banktest.ir/melli/sadad.shaparak.ir/VPG'

      return {
        paymentRequest: `${base}/api/v0/Request/PaymentRequest`,
        verify: `${base}/api/v0/Advice/Verify`,
        purchase: `${base}/Purchase`,
      }
    },
  },
  async mounted() {
    await this.loadConfig()
  },
  methods: {
    async loadConfig() {
      try {
        this.loading = true
        const res = await PaymentService.getConfig()

        if (res.status === 'success') {
          this.configExists = res.result.exists
          if (res.result.config) {
            // بررسی اینکه کلید ذخیره شده یا نه
            this.keySaved = !!(res.result.config.terminalKey && res.result.config.terminalKey.startsWith('****'))
            this.config = {
              ...this.config,
              ...res.result.config,
              // کلید ترمینال را خالی بگذار اگر masked است
              terminalKey: this.keySaved ? '' : res.result.config.terminalKey,
            }
            this.gatewayEnabled = res.result.config.enabled
          }
        }
      } catch (error) {
        console.error('Error loading config:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'خطا',
            text: 'خطا در بارگذاری تنظیمات',
            icon: 'XIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.loading = false
      }
    },

    async saveConfig() {
      if (!this.config.merchantId || !this.config.terminalId || !this.config.callbackUrl) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'خطا',
            text: 'لطفاً تمام فیلدهای ضروری را پر کنید',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
        return
      }

      // اگر کلید ترمینال خالی است و قبلاً تنظیم شده، نباید اجازه ذخیره بدهیم
      if (!this.config.terminalKey && !this.configExists) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'خطا',
            text: 'کلید ترمینال الزامی است',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
        return
      }

      try {
        this.saveLoading = true

        const dataToSave = { ...this.config }
        // اگر کلید خالی است، کلید قبلی را نگه دار
        if (!dataToSave.terminalKey && this.configExists) {
          delete dataToSave.terminalKey
        }

        const res = await PaymentService.saveConfig(dataToSave)

        if (res.status === 'success') {
          this.configExists = true
          // اگر کلید جدید وارد شده، آن را به عنوان ذخیره شده علامت بزن و فیلد را خالی کن
          if (this.config.terminalKey) {
            this.keySaved = true
            this.config.terminalKey = ''
          }
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'موفق',
              text: res.result || 'تنظیمات ذخیره شد',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        } else {
          throw new Error(res.message)
        }
      } catch (error) {
        console.error('Error saving config:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'خطا',
            text: error.message || 'خطا در ذخیره تنظیمات',
            icon: 'XIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.saveLoading = false
      }
    },

    async testConnection() {
      try {
        this.testLoading = true
        this.testResult = null

        const res = await PaymentService.testConnection()

        this.testResult = {
          success: res.status === 'success' && res.result.success,
          message: res.result.message || (res.result.success ? 'اتصال برقرار است' : 'خطا در اتصال'),
        }
      } catch (error) {
        console.error('Error testing connection:', error)
        this.testResult = {
          success: false,
          message: error.message || 'خطا در تست اتصال',
        }
      } finally {
        this.testLoading = false
      }
    },

    async toggleGateway() {
      try {
        this.toggleLoading = true

        const res = await PaymentService.toggleGateway({
          enabled: !this.gatewayEnabled,
        })

        if (res.status === 'success') {
          this.gatewayEnabled = res.result.enabled
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'موفق',
              text: res.result.message,
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        } else {
          throw new Error(res.message)
        }
      } catch (error) {
        console.error('Error toggling gateway:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'خطا',
            text: error.message || 'خطا در تغییر وضعیت درگاه',
            icon: 'XIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.toggleLoading = false
      }
    },

    copyUrl(url) {
      navigator.clipboard.writeText(url).then(() => {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'کپی شد',
            text: 'آدرس در کلیپ‌بورد کپی شد',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      })
    },
  },
}
</script>

<style scoped>
#payment-config {
  min-height: 100vh;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.header-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.status-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  font-size: 13px;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

/* Gateway Status Card */
.gateway-status-card {
  background: linear-gradient(135deg, #22c55e, #16a34a);
  border-radius: 16px;
  padding: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: white;
  flex-wrap: wrap;
  gap: 16px;
}

.gateway-status-card.disabled {
  background: linear-gradient(135deg, #64748b, #475569);
}

.gateway-status-info {
  display: flex;
  align-items: center;
  gap: 16px;
}

.gateway-status-icon {
  width: 56px;
  height: 56px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.gateway-status-text h5 {
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 4px;
}

.gateway-status-text p {
  margin: 0;
  opacity: 0.9;
  font-size: 14px;
}

/* Config Cards */
.config-card {
  border-radius: 16px;
  border: 1px solid #e2e8f0;
}

.config-card >>> .card-header {
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  padding: 16px 20px;
}

.card-header-content {
  display: flex;
  align-items: center;
}

.card-title {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  display: block;
}

.card-subtitle {
  font-size: 12px;
  color: #64748b;
}

/* Environment Toggle */
.env-toggle {
  display: flex;
  gap: 16px;
}

.env-option {
  flex: 1;
  padding: 16px 20px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 12px;
}

.env-option:hover {
  border-color: #0ea5e9;
}

.env-option.active {
  border-color: #0ea5e9;
  background: #e0f2fe;
}

.env-option input {
  display: none;
}

.env-radio {
  width: 20px;
  height: 20px;
  border: 2px solid #e2e8f0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.env-option.active .env-radio {
  border-color: #0ea5e9;
  background: #0ea5e9;
}

.env-radio::after {
  content: '';
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: white;
  opacity: 0;
}

.env-option.active .env-radio::after {
  opacity: 1;
}

.env-label {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}

.env-desc {
  font-size: 12px;
  color: #64748b;
}

/* URL Info */
.url-info {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.url-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.url-label {
  font-size: 13px;
  color: #64748b;
}

.url-display {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #1e293b;
  border-radius: 8px;
  padding: 10px 12px;
}

.url-display code {
  flex: 1;
  font-size: 12px;
  color: #94a3b8;
  direction: ltr;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Test Result */
.test-result {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  border-radius: 10px;
  margin-bottom: 20px;
}

.test-result.success {
  background: #dcfce7;
  color: #16a34a;
}

.test-result.error {
  background: #fee2e2;
  color: #dc2626;
}

/* Card Actions */
.card-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
  margin-top: 20px;
}

/* Form Label */
>>> .form-group label.required::after {
  content: ' *';
  color: #ef4444;
}

/* Responsive */
@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .gateway-status-card {
    flex-direction: column;
    align-items: flex-start;
  }

  .env-toggle {
    flex-direction: column;
  }

  .card-actions {
    flex-direction: column;
  }

  .card-actions .btn {
    width: 100%;
  }
}
</style>
